import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { map, tap, catchError } from 'rxjs/operators';
import { WorkflowElement } from '@workflow/models';
import { CanvasCoordinates } from '@view-model';
import { gql } from 'apollo-angular';
import { WorkflowElementCreateService, ElementCreateData } from '@element/services';
import { WorkflowElementConnectionService } from '@workflow/workflow-element-configuration/services/workflow-element-connection.service';

@Injectable({
  providedIn: 'root',
})
export class ElementListService {
  private apollo = inject(Apollo);
  private createService = inject(WorkflowElementCreateService);
  private connectionService = inject(WorkflowElementConnectionService);

  private elementsSubject = new BehaviorSubject<WorkflowElement[]>([]);
  private isUpdateInProgressSubject = new BehaviorSubject<boolean>(false);
  private isCanvasLockingOperationSubject = new BehaviorSubject<boolean>(false);
  private availableTypesSubject = new BehaviorSubject<any[]>([]);

  getWorkflowElementList(): Observable<WorkflowElement[]> {
    return this.elementsSubject.asObservable();
  }

  isUpdateInProgress(): Observable<boolean> {
    return this.isUpdateInProgressSubject.asObservable();
  }

  isCanvasLockingOperation(): Observable<boolean> {
    return this.isCanvasLockingOperationSubject.asObservable();
  }

  getAvailableTypes(): Observable<any[]> {
    return this.availableTypesSubject.asObservable();
  }

  loadWorkflowElements(elements: WorkflowElement[]): void {
    // Create mutable copies of elements to avoid read-only property errors
    const mutableElements = (elements || []).map((element) => ({
      ...element,
      view: element.view ? { ...element.view } : { positionX: 0, positionY: 0 },
      workflow: element.workflow ? { ...element.workflow } : { id: '' },
      nextElements: element.nextElements ? [...element.nextElements] : [],
      validation: element.validation
        ? {
            ...element.validation,
            fieldExceptions: element.validation.fieldExceptions
              ? [...element.validation.fieldExceptions]
              : [],
          }
        : { isValid: true, fieldExceptions: [] },
      // Copy any additional properties that might exist
      ...((element as any).onFalseNextElements && {
        onFalseNextElements: [...(element as any).onFalseNextElements],
      }),
    }));

    this.elementsSubject.next(mutableElements);
  }

  loadAvailableWorkflowElementTypes(): void {
    this.apollo
      .query({
        query: GET_AVAILABLE_WORKFLOW_ELEMENT_TYPES,
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((result: any) => result.data?.getAutomatorAvailableWorkflowElementTypes || []),
        tap((types) => this.availableTypesSubject.next(types)),
      )
      .subscribe();
  }

  createElement(
    positionX: number,
    positionY: number,
    type: string,
    name: string,
    workflowId: string,
  ): Observable<WorkflowElement> {
    // Create a temporary element with a temporary ID for immediate display
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const tempElement: WorkflowElement = {
      id: tempId,
      name,
      type,
      view: { positionX, positionY },
      workflow: { id: workflowId },
      nextElements: [],
      validation: { isValid: true, fieldExceptions: [] },
      // Mark as temporary for UI indication
      __isTemporary: true,
    } as any;

    // Add temporary element to UI immediately
    const currentElements = this.elementsSubject.value;
    this.elementsSubject.next([...currentElements, tempElement]);

    // Start background GraphQL operation and lock canvas for create operations
    this.isUpdateInProgressSubject.next(true);
    this.isCanvasLockingOperationSubject.next(true);

    const createData: ElementCreateData = {
      name,
      view: { positionX, positionY },
      workflowReference: { id: parseInt(workflowId, 10) },
    };

    // Perform GraphQL creation in background
    this.createService
      .createWorkflowElement(createData, type as any)
      .pipe(
        tap((realElement) => {
          if (realElement) {
            // Replace temporary element with real element from server
            const elements = this.elementsSubject.value;
            const updatedElements = elements.map((el) =>
              el.id === tempId ? { ...realElement, __isTemporary: false } : el,
            );
            this.elementsSubject.next(updatedElements);
          }
          this.isUpdateInProgressSubject.next(false);
          this.isCanvasLockingOperationSubject.next(false);
        }),
        catchError((error) => {
          // Remove temporary element on error
          const elements = this.elementsSubject.value;
          const updatedElements = elements.filter((el) => el.id !== tempId);
          this.elementsSubject.next(updatedElements);
          this.isUpdateInProgressSubject.next(false);
          this.isCanvasLockingOperationSubject.next(false);
          console.error('Error creating workflow element:', error);
          return throwError(() => error);
        }),
      )
      .subscribe();

    // Return the temporary element immediately for instant feedback
    return new Observable((subscriber) => {
      subscriber.next(tempElement);
      subscriber.complete();
    });
  }

  removeElement(elementId: string): Observable<void> {
    this.isUpdateInProgressSubject.next(true);
    this.isCanvasLockingOperationSubject.next(true);

    return this.apollo
      .mutate({
        mutation: DELETE_WORKFLOW_ELEMENT,
        variables: { id: parseInt(elementId, 10) },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        tap(() => {
          const currentElements = this.elementsSubject.value;
          const updatedElements = currentElements.filter((el) => el.id !== elementId);
          this.elementsSubject.next(updatedElements);
          this.isUpdateInProgressSubject.next(false);
          this.isCanvasLockingOperationSubject.next(false);
        }),
        catchError((error) => {
          this.isUpdateInProgressSubject.next(false);
          this.isCanvasLockingOperationSubject.next(false);
          console.error('Error removing workflow element:', error);
          return throwError(() => error);
        }),
        map(() => void 0),
      );
  }

  updateElementPosition(elementId: string, coordinates: CanvasCoordinates): Observable<void> {
    const { x, y } = coordinates.getCoordinates();
    this.isUpdateInProgressSubject.next(true);

    // Update position immediately in local state (optimistic update)
    const currentElements = this.elementsSubject.value;
    const updatedElements = currentElements.map((el) =>
      el.id === elementId ? { ...el, view: { ...el.view, positionX: x, positionY: y } } : el,
    );
    this.elementsSubject.next(updatedElements);

    return this.apollo
      .mutate({
        mutation: UPDATE_WORKFLOW_ELEMENT_POSITION,
        variables: {
          id: parseInt(elementId, 10),
          data: {
            view: {
              positionX: x,
              positionY: y,
            },
          },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        tap(() => {
          this.isUpdateInProgressSubject.next(false);
        }),
        catchError((error) => {
          this.isUpdateInProgressSubject.next(false);
          console.error('Error updating element position:', error);
          // On error, revert the position change
          const revertedElements = this.elementsSubject.value.map((el) =>
            el.id === elementId
              ? {
                  ...el,
                  view: {
                    ...el.view,
                    positionX: el.view?.positionX || 0,
                    positionY: el.view?.positionY || 0,
                  },
                }
              : el,
          );
          this.elementsSubject.next(revertedElements);
          return throwError(() => error);
        }),
        map(() => void 0),
      );
  }

  createElementConnection(
    startElementId: string,
    nextElementsPropertyName: string,
    endElementId: string,
  ): Observable<void> {
    this.isUpdateInProgressSubject.next(true);

    // Update local state immediately for better UX
    const currentElements = this.elementsSubject.value;
    const updatedElements = currentElements.map((el) => {
      if (el.id === startElementId) {
        // Use the correct property name instead of always using 'nextElements'
        const currentPropertyValue = (el as any)[nextElementsPropertyName] || [];

        // Check if connection already exists
        if (!currentPropertyValue.find((ne: any) => ne?.id === endElementId)) {
          currentPropertyValue.push({ id: endElementId });
        }

        // Return updated element with the correct property updated
        return {
          ...el,
          [nextElementsPropertyName]: currentPropertyValue,
        };
      }
      return el;
    });

    this.elementsSubject.next(updatedElements);

    // Find the updated element to send to GraphQL
    const updatedElement = updatedElements.find((el) => el.id === startElementId);
    if (!updatedElement) {
      this.isUpdateInProgressSubject.next(false);
      return throwError(() => new Error('Element not found'));
    }

    // Use the connection service to update the backend
    return this.connectionService.updateElementConnection(updatedElement).pipe(
      tap(() => {
        this.isUpdateInProgressSubject.next(false);
      }),
      catchError((error) => {
        this.isUpdateInProgressSubject.next(false);
        console.error('Error creating element connection:', error);

        // Revert the local state change on error
        const revertedElements = this.elementsSubject.value.map((el) => {
          if (el.id === startElementId) {
            const currentPropertyValue = (el as any)[nextElementsPropertyName] || [];
            const filteredPropertyValue = currentPropertyValue.filter(
              (ne: any) => ne?.id !== endElementId,
            );
            return {
              ...el,
              [nextElementsPropertyName]: filteredPropertyValue,
            };
          }
          return el;
        });
        this.elementsSubject.next(revertedElements);

        return throwError(() => error);
      }),
      map(() => void 0),
    );
  }

  removeElementConnection(
    startElementId: string,
    nextElementsPropertyName: string,
    endElementId: string,
  ): Observable<void> {
    this.isUpdateInProgressSubject.next(true);

    // Update local state immediately for better UX
    const currentElements = this.elementsSubject.value;
    const updatedElements = currentElements.map((el) => {
      if (el.id === startElementId) {
        // Use the correct property name instead of always using 'nextElements'
        const currentPropertyValue = (el as any)[nextElementsPropertyName] || [];
        const filteredPropertyValue = currentPropertyValue.filter(
          (ne: any) => ne?.id !== endElementId,
        );

        // Return updated element with the correct property updated
        return {
          ...el,
          [nextElementsPropertyName]: filteredPropertyValue,
        };
      }
      return el;
    });

    this.elementsSubject.next(updatedElements);

    // Find the updated element to send to GraphQL
    const updatedElement = updatedElements.find((el) => el.id === startElementId);
    if (!updatedElement) {
      this.isUpdateInProgressSubject.next(false);
      return throwError(() => new Error('Element not found'));
    }

    // Use the connection service to update the backend
    return this.connectionService.updateElementConnection(updatedElement).pipe(
      tap(() => {
        this.isUpdateInProgressSubject.next(false);
      }),
      catchError((error) => {
        this.isUpdateInProgressSubject.next(false);
        console.error('Error removing element connection:', error);

        // Revert the local state change on error
        const revertedElements = this.elementsSubject.value.map((el) => {
          if (el.id === startElementId) {
            const currentPropertyValue = (el as any)[nextElementsPropertyName] || [];
            // Re-add the connection that was removed
            if (!currentPropertyValue.find((ne: any) => ne?.id === endElementId)) {
              currentPropertyValue.push({ id: endElementId });
            }
            return {
              ...el,
              [nextElementsPropertyName]: currentPropertyValue,
            };
          }
          return el;
        });
        this.elementsSubject.next(revertedElements);

        return throwError(() => error);
      }),
      map(() => void 0),
    );
  }
}

// GraphQL Queries
const GET_AVAILABLE_WORKFLOW_ELEMENT_TYPES = gql`
  query getAvailableWorkflowElementTypes {
    getAutomatorAvailableWorkflowElementTypes {
      targetType
      isAvailable
      reasons
    }
  }
`;

const DELETE_WORKFLOW_ELEMENT = gql`
  mutation deleteWorkflowElement($id: Long!) {
    deleteAutomatorWorkflowElement(id: $id)
  }
`;

const UPDATE_WORKFLOW_ELEMENT_POSITION = gql`
  mutation updateWorkflowElementPosition($id: Long!, $data: AutomatorUpdateWorkflowElementInput!) {
    updateAutomatorWorkflowElement(id: $id, data: $data) {
      id
      view {
        positionX
        positionY
      }
    }
  }
`;
